import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useUniversity } from '../../contexts/UniversityContext';
import { FaSearch, FaGlobeAmericas, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  display: ${props => (props.$show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  overflow: hidden;
  animation: fadeIn 0.3s ease;
  
  @media (max-width: 480px) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }
  
  @media (max-width: 320px) {
    padding: 5px;
    align-items: flex-start;
    padding-top: 10px;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 15px;
    align-items: flex-start;
    padding-top: 30px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    align-items: flex-start;
    padding-top: 30px;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.card};
  border-radius: 12px;
  padding: 0;
  max-width: 550px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s ease;
  overflow-y: auto;
  overflow-x: hidden;
  border: 3px solid #4CAF50;
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 85vh;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    border-radius: 10px;
    max-height: 95vh;
    border-width: 2px;
  }
  
  @media (max-width: 320px) {
    border-width: 1px;
    border-radius: 8px;
    max-height: 98vh;
  }
  
  @media screen and (max-height: 600px) {
    max-height: 95vh;
  }
  
  @media screen and (max-height: 500px) {
    max-height: 98vh;
  }
  
  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${props => props.theme.card};
  padding: 20px 20px 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    padding: 15px 15px 0;
  }
  
  @media (max-width: 480px) {
    padding: 12px 12px 0;
  }
  
  @media (max-width: 320px) {
    padding: 10px 8px 0;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    padding: 15px 20px 0;
  }
`;

const ModalScrollArea = styled.div`
  padding: 15px 15px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  max-height: 60vh;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 8px;
  }
  
  @media (max-width: 320px) {
    padding: 8px 5px;
  }
`;

const ModalFooter = styled.div`
  padding: 12px 20px 15px;
  margin-top: 10px;
  border-top: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.card};
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    margin-top: 8px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    margin-top: 4px;
    gap: 6px;
  }
  
  @media screen and (max-height: 600px) {
    padding: 8px;
    margin-top: 2px;
  }
  
  @media (min-width: 1200px) and (max-height: 800px) {
    grid-column: 1 / 3;
    grid-row: 3;
    margin-top: 0;
  }
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: clamp(18px, 4.5vw, 24px);
  margin-bottom: 8px;
  font-weight: 700;
  
  @media (max-width: 480px) {
    margin-bottom: 6px;
    font-size: clamp(16px, 4vw, 18px);
  }
  
  @media (max-width: 320px) {
    margin-bottom: 4px;
    font-size: clamp(14px, 3.8vw, 16px);
  }
`;

const ModalSubtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: clamp(13px, 3.2vw, 15px);
  line-height: 1.4;
  margin: 0 0 15px 0;
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
    font-size: clamp(12px, 3vw, 13px);
    line-height: 1.3;
  }
  
  @media (max-width: 320px) {
    margin-bottom: 8px;
    font-size: clamp(10px, 2.8vw, 12px);
    line-height: 1.2;
  }
  
  @media screen and (max-height: 500px) {
    margin-bottom: 5px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
  
  @media (max-width: 320px) {
    margin-bottom: 6px;
  }
  
  @media screen and (max-height: 600px) {
    margin-bottom: 6px;
  }
  
  @media screen and (max-height: 500px) {
    margin-bottom: 4px;
  }
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: ${props => props.$active ? props.theme.primary : props.theme.buttonLight};
  color: ${props => props.$active ? 'white' : props.theme.buttonText};
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;
  margin-right: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  user-select: none;
  min-width: 60px;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.primaryHover : props.theme.buttonLightHover};
  }
  
  svg {
    font-size: 16px;
  }
  
  @media (min-width: 768px) {
    padding: 7px 14px;
    font-size: 15px;
    gap: 7px;
    border-radius: 18px;
    min-width: 70px;
    
    svg {
      font-size: 17px;
    }
  }
  
  @media (min-width: 1024px) {
    padding: 8px 16px;
    font-size: 16px;
    gap: 8px;
    min-width: 80px;
    
    svg {
      font-size: 18px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    margin-right: 5px;
    margin-bottom: 5px;
    border-radius: 12px;
    font-size: 12px;
    min-width: 50px;
    
    svg {
      font-size: 14px;
    }
  }
  
  @media (max-width: 320px) {
    padding: 3px 6px;
    margin-right: 4px;
    margin-bottom: 4px;
    border-radius: 10px;
    font-size: 10px;
    min-width: 40px;
    
    svg {
      font-size: 12px;
    }
  }
`;

const GridContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding-bottom: 10px;
  box-sizing: border-box;
`;

const UniversityList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
  
  @media (max-width: 320px) {
    gap: 8px;
  }
`;

const UniversityCard = styled.div`
  padding: 10px 5px;
  border-radius: 8px;
  background: ${props => props.$selected ? props.theme.primaryLight : props.theme.background};
  border: 2px solid ${props => props.$selected ? props.theme.primary : props.theme.border};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  aspect-ratio: 1 / 1;
  box-sizing: border-box;
  user-select: none;
  overflow: hidden;
  
  @media (max-width: 480px) {
    padding: 8px 4px;
    border-radius: 6px;
    border-width: 1.5px;
  }
  
  @media (max-width: 375px) {
    padding: 6px 3px;
    border-radius: 5px;
    border-width: 1px;
  }
  
  @media (max-width: 320px) {
    padding: 4px 2px;
    border-radius: 4px;
    border-width: 1px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.primary};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const UniversityLogo = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: ${props => props.$color || '#1877f2'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(16px, 4vw, 20px);
  font-weight: bold;
  margin-bottom: 6px;
  overflow: hidden;
  user-select: none;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    background-color: white;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    pointer-events: none;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    margin-bottom: 4px;
    font-size: 16px;
  }
  
  @media (max-width: 320px) {
    width: 28px;
    height: 28px;
    margin-bottom: 3px;
    font-size: 14px;
  }
  
  @media screen and (max-height: 500px) {
    width: 24px;
    height: 24px;
    margin-bottom: 2px;
    font-size: 12px;
  }
`;

const UniversityName = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
  user-select: none;
  pointer-events: none;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding: 0 4px;
  box-sizing: border-box;
  line-height: 1.2;
  
  @media (max-width: 480px) {
    margin-bottom: 3px;
    font-size: 12px;
  }
  
  @media (max-width: 320px) {
    margin-bottom: 2px;
    font-size: 11px;
  }
  
  @media (min-width: 768px) {
    font-size: 15px;
  }
  
  @media (min-width: 1024px) {
    font-size: 16px;
  }
`;

const UniversityLocation = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  user-select: none;
  pointer-events: none;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding: 0 4px;
  box-sizing: border-box;
  line-height: 1.2;
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
  
  @media (max-width: 320px) {
    font-size: 9px;
  }
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
  
  @media (min-width: 1024px) {
    font-size: 14px;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 8px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: clamp(13px, 3.2vw, 14px);
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 5px;
  }
  
  @media (max-width: 320px) {
    padding: 6px;
    border-radius: 4px;
    font-size: clamp(11px, 3vw, 12px);
  }
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
  }
  
  &:disabled {
    background-color: ${props => props.theme.disabled};
    cursor: not-allowed;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 15px;
  width: 100%;
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
  
  @media (max-width: 320px) {
    margin-bottom: 6px;
  }
  
  @media screen and (max-height: 600px) {
    margin-bottom: 8px;
  }
  
  @media screen and (max-height: 500px) {
    margin-bottom: 5px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textSecondary};
  pointer-events: none;
  font-size: clamp(14px, 3.5vw, 16px);
  
  @media (max-width: 480px) {
    left: 8px;
    font-size: 14px;
  }
  
  @media (max-width: 320px) {
    left: 6px;
    font-size: 12px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 10px 10px 36px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  font-size: 16px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primaryLight};
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    opacity: 0.8;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  
  @media (max-width: 480px) {
    padding: 8px 8px 8px 30px;
    height: 38px;
    border-radius: 6px;
    
    &::placeholder {
      font-size: 14px;
    }
  }
  
  @media (max-width: 400px) {
    &::placeholder {
      font-size: 13px;
    }
  }
  
  @media (max-width: 350px) {
    padding: 8px 8px 8px 26px;
    
    &::placeholder {
      font-size: 12px;
    }
  }
  
  @media (max-width: 320px) {
    padding: 6px 6px 6px 24px;
    height: 32px;
    border-radius: 4px;
    
    &::placeholder {
      font-size: 11px;
    }
  }
  
  @media screen and (max-height: 500px) {
    height: 32px;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 30px 0;
  color: ${props => props.theme.textSecondary};
  user-select: none;
  font-size: clamp(13px, 3.2vw, 15px);
  
  @media (max-width: 480px) {
    padding: 20px 0;
  }
  
  @media (max-width: 320px) {
    padding: 15px 0;
    font-size: clamp(11px, 3vw, 12px);
  }
  
  @media screen and (max-height: 500px) {
    padding: 10px 0;
  }
`;

const UniversitySelectModal = ({ show, onClose, isRequired = false, onOpenAuthModal }) => {
  const { universities, selectedUniversity, changeUniversity, isChanging } = useUniversity();
  const { user, isAuthenticated } = useAuth();
  const [tempSelection, setTempSelection] = useState(selectedUniversity || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeRegion, setActiveRegion] = useState('Todas');
  const [isReopened, setIsReopened] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Generate a unique ID for this form instance
  const modalId = React.useMemo(() => `university-modal-${Math.random().toString(36).substr(2, 9)}`, []);
  
  // Lista de regiones disponibles basada en las universidades
  const regions = useMemo(() => {
    const regionSet = new Set(['Todas']);
    universities.forEach(uni => {
      // Extraer región/país de la ubicación
      const region = uni.location.split(',').pop().trim();
      if (region) regionSet.add(region);
    });
    return Array.from(regionSet);
  }, [universities]);
  
  // Actualizar la selección temporal cuando cambia la selección actual o cuando el modal se muestra
  useEffect(() => {
    if (show) {
      setTempSelection(selectedUniversity || '');
      setSearchQuery('');
      // Set isReopened to true if there's a selectedUniversity
      setIsReopened(!!selectedUniversity);
    }
  }, [selectedUniversity, show]);
  
  // Efecto para prevenir el scroll del body cuando el modal está abierto
  useEffect(() => {
    if (show) {
      // Guardar el valor original de overflow
      const originalStyle = window.getComputedStyle(document.body).overflow;
      // Prevenir scroll
      document.body.style.overflow = 'hidden';
      
      // Restaurar cuando el componente se desmonte o el modal se cierre
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [show]);
  
  // Efecto para seleccionar automáticamente la universidad del usuario cuando inicia sesión
  useEffect(() => {
    if (isAuthenticated && user && user.university) {
      // Verificar si la universidad del usuario existe en nuestra lista
      const userUniversity = universities.find(uni => uni.id === user.university);
      if (userUniversity) {
        setTempSelection(user.university);
      }
    }
  }, [isAuthenticated, user, universities]);
  
  // Filtrar universidades basado en búsqueda y región
  const filteredUniversities = useMemo(() => {
    // First filter the universities
    const filtered = universities.filter(uni => {
      const matchesSearch = searchQuery === '' || 
        uni.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        uni.location.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRegion = activeRegion === 'Todas' || 
        uni.location.includes(activeRegion);
      
      return matchesSearch && matchesRegion;
    });
    
    // Shuffle the filtered results
    const shuffled = [...filtered].sort(() => Math.random() - 0.5);
    
    // If the modal is reopened and there's a selected university, move it to the top
    if (isReopened && selectedUniversity) {
      const selectedIndex = shuffled.findIndex(uni => uni.id === selectedUniversity);
      if (selectedIndex !== -1) {
        const selectedUni = shuffled.splice(selectedIndex, 1)[0];
        shuffled.unshift(selectedUni);
      }
    }
    
    return shuffled;
  }, [universities, searchQuery, activeRegion, selectedUniversity, isReopened]);
  
  // Manejar la selección simple (solo marca como seleccionada temporalmente)
  const handleSelection = (universityId) => {
    if (!isProcessing && !isChanging) {
      setTempSelection(universityId);
      handleConfirm(universityId);
    }
  };
  
  // Confirmar la selección actual y cerrar
  const handleConfirm = (selectedId = null) => {
    const univId = selectedId || tempSelection;
    
    if (univId && !isProcessing && !isChanging) {
      // Prevent multiple clicks
      setIsProcessing(true);
      
      // First update state and localStorage
      changeUniversity(univId);
      localStorage.setItem('selectedUniversity', univId);
      localStorage.setItem('hasSelectedUniversity', 'true');
      
      // Remove scroll to top when changing university
      
      // Add visitor mode notification if selected university
      // doesn't match the logged in user's university
      if (isAuthenticated && user && user.university && univId !== user.university) {
        // Optional: display some notification or toast
        console.log('Estás navegando en modo visitante en otra universidad');
      }
      
      // Close the modal with a slight delay to allow the university change to propagate
      setTimeout(() => {
        onClose();
        setIsProcessing(false);
      }, 100);
    }
  };
  
  // Prevent closing the modal when:
  // 1. isRequired is true (no university selected yet)
  // 2. Clicking outside the modal content
  const handleOverlayClick = (e) => {
    if (!isRequired && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const renderUniversityCard = (university) => (
    <UniversityCard 
      key={university.id}
      $selected={tempSelection === university.id}
      onClick={() => handleSelection(university.id)}
      role="button"
      aria-pressed={tempSelection === university.id}
    >
      <UniversityLogo $color={university.color}>
        {university.logo ? (
          <img 
            src={university.logo}
            alt={university.name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              borderRadius: '50%',
              pointerEvents: 'none'
            }}
            draggable="false"
            loading="lazy"
            crossOrigin="anonymous"
          />
        ) : (
          university.name.charAt(0)
        )}
      </UniversityLogo>
      <UniversityName>{university.name}</UniversityName>
      <UniversityLocation>{university.location}</UniversityLocation>
    </UniversityCard>
  );
  
  return (
    <ModalOverlay $show={show} onClick={handleOverlayClick}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>¡Entérate de todo en tu universidad! 🎯</ModalTitle>
          <ModalSubtitle>
            {isRequired
              ? "No te pierdas ningún chisme, historia o secreto de tu campus. ¡Únete a la comunidad más grande de confesiones universitarias! 🤫"
              : "Descubre los secretos mejor guardados, confesiones anónimas y todo lo que pasa en tu universidad 🔥"}
          </ModalSubtitle>
          
          {/* Búsqueda */}
          <SearchContainer>
            <SearchIcon>
              <FaSearch />
            </SearchIcon>
            <SearchInput 
              type="text"
              placeholder="¿En qué universidad estudias? 🔍"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              id={`${modalId}-search`}
              name="university-search"
            />
          </SearchContainer>
          
          {/* Filtros por región */}
          <FilterContainer>
            {regions.map((region) => (
              <FilterChip
                key={region}
                $active={activeRegion === region}
                onClick={() => setActiveRegion(region)}
              >
                {region === 'Todas' ? <FaGlobeAmericas /> : null}
                {region}
              </FilterChip>
            ))}
          </FilterContainer>
        </ModalHeader>

        <ModalScrollArea>
          {filteredUniversities.length > 0 ? (
            <GridContainer>
              <UniversityList>
                {filteredUniversities.map(university => 
                  renderUniversityCard(university)
                )}
              </UniversityList>
            </GridContainer>
          ) : (
            <NoResults>
              <FaTimes size={24} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No se encontraron universidades</p>
              <p style={{ fontSize: '14px', marginTop: '5px' }}>Intenta con otra búsqueda o filtro</p>
            </NoResults>
          )}
        </ModalScrollArea>
        
        <ModalFooter>
          <ConfirmButton 
            onClick={() => handleConfirm()}
            disabled={!tempSelection || isProcessing || isChanging}
          >
            {isProcessing ? 'Procesando...' : 'Seleccionar'}
          </ConfirmButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UniversitySelectModal;